import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FaBug, FaLightbulb, FaPaperPlane } from 'react-icons/fa';
import Button from '../../../components/misc/Button';
import { FeedbackOption, sendFeedback } from '../../../services/feedbackService';
import * as constants from '../../../constants';

export default function FeedbackPage(): JSX.Element {
  const { t } = useTranslation();
  const [feedback, setFeedback] = useState<string>('');
  const [option, setOption] = useState<FeedbackOption>(FeedbackOption.FEATURE);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const placeholder =
    option === FeedbackOption.FEATURE ? t('feedback.featurePlaceholder') : t('feedback.bugPlaceholder');

  const handleSendFeedback = async () => {
    if (!feedback.trim()) {
      toast.error(t('feedback.toast.empty'));
      return;
    }

    setIsSubmitting(true);
    try {
      await sendFeedback(feedback, option);
      toast.success(t('feedback.toast.success'));
      setFeedback('');
    } catch (error) {
      toast.error(t('feedback.toast.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex h-full flex-col items-center justify-center px-4 py-8 md:py-16">
      <img src="/images/figures/mess.svg" alt="Vera Messy" className="h-48 w-48 object-contain md:h-64 md:w-64" />

      {/* Option Selection */}
      <h2 className="mb-4 text-xl font-semibold text-primary md:text-2xl">{t('feedback.title')}</h2>
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => setOption(FeedbackOption.FEATURE)}
          aria-pressed={option === FeedbackOption.FEATURE}
          className="flex flex-col items-center gap-2 transition-transform hover:scale-105">
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-full border-2 transition-all md:h-16 md:w-16 ${
              option === FeedbackOption.FEATURE ? 'bg-primary-300' : 'bg-transparent'
            }`}>
            <FaLightbulb
              size={28}
              color={option === FeedbackOption.FEATURE ? constants.COLOR_SECONDARY : constants.COLOR_PRIMARY_300}
            />
          </div>
          <span className="text-sm font-medium text-primary">{t('feedback.feature')}</span>
        </button>

        <button
          type="button"
          onClick={() => setOption(FeedbackOption.BUG)}
          aria-pressed={option === FeedbackOption.BUG}
          className="flex flex-col items-center gap-2 transition-transform hover:scale-105">
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-full border-2 transition-all md:h-16 md:w-16 ${
              option === FeedbackOption.BUG ? 'bg-primary-300' : 'bg-transparent'
            }`}>
            <FaBug
              size={28}
              color={option === FeedbackOption.BUG ? constants.COLOR_SECONDARY : constants.COLOR_PRIMARY_300}
            />
          </div>
          <span className="text-sm font-medium text-primary">{t('feedback.bug')}</span>
        </button>
      </div>

      {/* Feedback Input */}
      <textarea
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        placeholder={placeholder}
        className="my-4 h-36 w-full max-w-2xl rounded-lg border-none bg-secondary-50 p-4 text-primary focus:border-primary focus:ring-0 sm:h-44 sm:w-2/3 md:w-1/3"
        disabled={isSubmitting}
      />

      {/* Submit Button */}
      <div className="flex justify-center sm:justify-end">
        <Button
          variant="primary"
          onClick={handleSendFeedback}
          disabled={isSubmitting || !feedback.trim()}
          className="w-full justify-center gap-2 px-6 sm:w-auto">
          <FaPaperPlane />
          {t('feedback.send')}
        </Button>
      </div>
    </div>
  );
}
